// Direct Google OAuth helper — uses the app's OWN Google Cloud OAuth credentials
// (NOT Emergent's hosted auth, which was flagged by Google policy).
//
// Flow:
//   1. startGoogleSignIn() generates a CSRF state, stores it in localStorage,
//      fetches the client_id from the backend, and redirects to Google at the
//      TOP browser window (escapes any iframe — Emergent preview pane,
//      in-app browsers, etc. — which Google blocks via "disallowed_useragent").
//   2. Google sends the user back to /auth/google/callback?code=...&state=...
//   3. The callback page (see AuthCallbackPage.js) verifies state, posts the code
//      to POST /api/auth/google, and the backend exchanges it for a JWT.
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
const STATE_STORAGE_KEY = 'google_oauth_state';

// REMINDER: DO NOT HARDCODE THE REDIRECT URI OR ADD FALLBACKS — this URL must EXACTLY
// match what's registered in Google Cloud Console → APIs & Services → Credentials.
export const buildRedirectUri = () => `${window.location.origin}/auth/google/callback`;

const randomState = () => {
  const bytes = new Uint8Array(24);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
};

// Navigate to Google at the topmost browser window. If we're inside an iframe (e.g.
// Emergent's preview pane), `window.top.location.href` escapes it. Without this,
// Google blocks the flow with "disallowed_useragent" / Error 403.
const navigateTopLevel = (url) => {
  try {
    if (window.top && window.top !== window) {
      window.top.location.href = url;
      return;
    }
  } catch (_e) {
    // Cross-origin frame — can't access window.top. Fall through.
  }
  window.location.href = url;
};

export const startGoogleSignIn = async () => {
  const { data } = await axios.get(`${API}/auth/google/config`);
  if (!data?.client_id) throw new Error('Google OAuth client_id missing from server config');

  const state = randomState();
  // localStorage (not sessionStorage) so the state survives if we navigate the top window
  // out from under our iframe context.
  localStorage.setItem(STATE_STORAGE_KEY, state);

  const params = new URLSearchParams({
    client_id: data.client_id,
    redirect_uri: buildRedirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'online',
    prompt: 'select_account',
    state,
  });
  navigateTopLevel(`${GOOGLE_AUTH_ENDPOINT}?${params.toString()}`);
};

export const consumeStoredState = () => {
  const stored = localStorage.getItem(STATE_STORAGE_KEY);
  localStorage.removeItem(STATE_STORAGE_KEY);
  return stored;
};
